import type { NewsAnalysis } from './types';
import { CheckCircle, XCircle, AlertTriangle } from '../../../icons';
import { scoreStyle, sentimentChip } from './colors';

/**
 * "News coverage" analysis card (Figma 2165-108189): status pills + summary +
 * key topics + topic distribution. The article carousel is a separate
 * "News Articles" section — this card holds analysis only.
 */
export default function NewsCoverageAnalysis({ analysis }: { analysis: NewsAnalysis }) {
  // No fabricated fallback — the score pill is hidden when the score is absent.
  const score = analysis.brand_safety_score ?? null;
  const scoreSty = score != null ? scoreStyle(score) : null;
  const hasIssues = analysis.risk_signals.length > 0;
  const needsReview =
    analysis.controversy_level !== 'None' && analysis.controversy_level !== 'Minor';

  const topicDist = [...analysis.topic_distribution]
    .sort((a, b) => b.count - a.count)
    .slice(0, 7);
  const topicMax = Math.max(1, ...topicDist.map((t) => t.count));

  return (
    <div className="mb-4 flex flex-col gap-4 rounded-2xl border border-border bg-card p-6">
      <div className="text-heading-h6 text-foreground">News coverage</div>

      {/* Status pills */}
      <div className="flex flex-wrap items-center gap-2">
        {score != null && scoreSty && (
          <StatusPill
            icon={<CheckCircle size={14} />}
            label={`${scoreSty.label} · ${Math.round(score)}/100`}
            color={scoreSty.color}
            background={scoreSty.bg}
            border={scoreSty.border}
          />
        )}
        {hasIssues ? (
          <StatusPill icon={<XCircle size={14} />} label="Issues found" color="#DC2626" background="#FEF2F2" border="#FECACA" />
        ) : (
          <StatusPill icon={<CheckCircle size={14} />} label="Zero issues found" color="#047857" background="#ECFDF5" border="#A7F3D0" />
        )}
        {needsReview && (
          <StatusPill icon={<AlertTriangle size={14} />} label="Needs review" color="#B45309" background="#FFFBEB" border="#FDE68A" />
        )}
      </div>

      {analysis.summary && (
        <p className="m-0 text-body-sm-regular leading-relaxed text-foreground">{analysis.summary}</p>
      )}

      {analysis.key_topics.length > 0 && (
        <div>
          <div className="mb-2 text-caption-medium uppercase tracking-wide text-muted-foreground">Key topics</div>
          <div className="flex flex-wrap gap-1.5">
            {analysis.key_topics.map((t) => (
              <span
                key={t}
                className="rounded-full border border-border bg-muted px-2.5 py-1 text-caption-medium text-foreground"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      )}

      {topicDist.length > 0 && (
        <div>
          <div className="mb-2 text-caption-medium uppercase tracking-wide text-muted-foreground">Topic distribution</div>
          <div className="flex flex-col gap-1.5">
            {topicDist.map((t) => {
              const chip = sentimentChip[t.sentiment];
              const pct = Math.max(4, (t.count / topicMax) * 100);
              return (
                <div key={t.topic} className="grid grid-cols-[1fr_auto] items-center gap-2">
                  <div>
                    <div className="mb-0.5 text-caption-medium text-foreground">{t.topic}</div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                      <div className="h-full transition-[width] duration-300" style={{ width: `${pct}%`, background: chip.color }} />
                    </div>
                  </div>
                  <div className="min-w-5 text-right text-caption-medium text-muted-foreground">{t.count}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function StatusPill({
  icon,
  label,
  color,
  background,
  border,
}: {
  icon: React.ReactNode;
  label: string;
  color: string;
  background: string;
  border: string;
}) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-caption-medium"
      style={{ color, background, border: `1px solid ${border}` }}
    >
      <span style={{ color }} className="inline-flex">{icon}</span>
      {label}
    </span>
  );
}
